import { parseServerDate } from './parseServerDate';

type Format = 'date' | 'time' | 'datetime' | 'UTC' | 'startRange' | 'endRange';

/** Shown instead of a date the backend sent in a shape we cannot read */
export const UNKNOWN_DATE = '—';

const DISPLAY_FORMATS: Format[] = ['date', 'time', 'datetime'];

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MINUTE = 60_000;

/** Minutes to add to local time to get UTC at that instant, as `Date#getTimezoneOffset` gives them */
type OffsetOf = (instant: Date) => number;
const browserOffsetOf: OffsetOf = (instant) => instant.getTimezoneOffset();

/**
 * A bound of the day a filter is set for, as the employee sees that day: the list shows local time, so the bounds are
 * the local midnights turned into UTC. A `yyyy-MM-dd` from a date input is that day as typed, not UTC midnight; a
 * timestamp stands for the local day it falls on.
 */
export const rangeBound = (
  date: string,
  edge: 'start' | 'end',
  offsetOf: OffsetOf = browserOffsetOf,
): string => {
  const match = DATE_ONLY.exec(date.trim());
  let wall: Date;
  if (match) {
    wall = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  } else {
    const instant = parseServerDate(date) ?? new Date();
    wall = new Date(instant.getTime() - offsetOf(instant) * MINUTE);
  }
  const bound = Date.UTC(
    wall.getUTCFullYear(),
    wall.getUTCMonth(),
    wall.getUTCDate(),
    edge === 'start' ? 0 : 23,
    edge === 'start' ? 0 : 59,
    edge === 'start' ? 0 : 59,
  );
  // the offset of the bound itself: a day with a clock change has different ones at its two ends
  return new Date(bound + offsetOf(new Date(bound)) * MINUTE).toISOString();
};

/** A day as a `<input type="date">` value, `yyyy-MM-dd`, in local time */
export const localDateInput = (date: Date, offsetOf: OffsetOf = browserOffsetOf) =>
  new Date(date.getTime() - offsetOf(date) * MINUTE).toISOString().slice(0, 10);

export const convertDateToNeedFormat = ({
  date,
  format = 'time',
}: {
  date: string;
  format?: Format;
}) => {
  const parsed = parseServerDate(date);
  // falling back to "now" would quietly show today instead of the value that failed to parse
  if (!parsed && DISPLAY_FORMATS.includes(format)) {
    return UNKNOWN_DATE;
  }
  const needDate = parsed ?? new Date();
  const hours = needDate.getHours() < 10 ? `0${needDate.getHours()}` : needDate.getHours();
  const minutes = needDate.getMinutes() < 10 ? `0${needDate.getMinutes()}` : needDate.getMinutes();
  const day = needDate.getDate() < 10 ? `0${needDate.getDate()}` : needDate.getDate();
  const month =
    needDate.getMonth() + 1 < 10 ? `0${needDate.getMonth() + 1}` : needDate.getMonth() + 1;
  const year = needDate.getFullYear();
  switch (format) {
    case 'date':
      return `${day}.${month}.${year}`;
    case 'time': {
      return `${hours}:${minutes}`;
    }
    case 'datetime': {
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
    case 'UTC': {
      const utcDate = Date.UTC(
        needDate.getUTCFullYear(),
        needDate.getUTCMonth(),
        needDate.getUTCDate(),
        needDate.getUTCHours(),
        needDate.getUTCMinutes(),
      );
      return new Date(utcDate).toISOString();
    }
    case 'startRange':
      return rangeBound(date, 'start');
    case 'endRange':
      return rangeBound(date, 'end');
  }
};

/** Whether two instants fall on the same local day, as the list shows them */
export const sameLocalDay = (start: string, end: string, offsetOf: OffsetOf = browserOffsetOf) => {
  const startInstant = parseServerDate(start);
  const endInstant = parseServerDate(end);
  if (!startInstant || !endInstant) {
    return false;
  }
  return localDateInput(startInstant, offsetOf) === localDateInput(endInstant, offsetOf);
};

/**
 * For values the backend may send either as a timestamp or as an already formatted string
 * (`InvoiceDto.date` is `dd.MM.yyyy` today): format what we can parse, show the rest untouched.
 */
export const formatServerDateOrRaw = (date?: string | null, format: Format = 'date') => {
  if (!date) {
    return UNKNOWN_DATE;
  }
  const formatted = convertDateToNeedFormat({ date, format });
  return formatted === UNKNOWN_DATE ? date : formatted;
};
